<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { IconName } from '@/libs/icons';

withDefaults(
  defineProps<{
    description?: string;
    /** Lucide icon name, for the recent-search variant */
    icon?: IconName;
    label: string;
    type?: 'stop-item' | 'recent-search';
  }>(),
  {
    description: undefined,
    icon: 'rotate-ccw-clock',
    type: 'stop-item',
  },
);
</script>

<template>
  <span class="list-item" :class="`list-item--${type}`">
    <template v-if="type === 'stop-item'">
      <span class="list-item__details">
        <span class="list-item__label">
          {{ label }}
          <slot name="label-suffix" />
        </span>
        <span v-if="$slots.badges" class="list-item__badges">
          <slot name="badges" />
        </span>
      </span>
      <slot name="trailing" />
    </template>

    <template v-else>
      <span class="list-item__icon-wrapper">
        <BaseIcon :name="icon" class="list-item__icon" />
      </span>
      <span class="list-item__details">
        <span v-if="$slots.badges" class="list-item__badges">
          <slot name="badges" />
        </span>
        <span class="list-item__label">{{ label }}</span>
        <span v-if="description" class="list-item__description">{{ description }}</span>
      </span>
    </template>
  </span>
</template>

<style lang="scss">
.list-item {
  align-items: center;
  display: flex;
  gap: $spacing-4;
  padding: $spacing-4 $spacing-5;

  &--recent-search {
    background-color: $color-white;

    .list-item__details {
      gap: $spacing-1;
    }
  }

  &__details {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }

  &__label {
    color: $color-text-dark;
    display: block;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
  }

  &__description {
    color: $color-neutral-higher;
    display: block;
    font: $text-small;
    letter-spacing: $text-small-spacing;
  }

  &__icon-wrapper {
    align-items: center;
    background-color: $color-neutral-lower;
    border-radius: $radius-pill;
    display: flex;
    flex-shrink: 0;
    height: 40px;
    justify-content: center;
    width: 40px;
  }

  &__icon {
    font-size: 20px;
  }
}
</style>
